'use client'

import { useTheme } from 'next-themes'
import { useEffect, useState } from 'react'
import { Monitor, Sun, Moon } from 'lucide-react'

// Three explicit modes, cycled by a single button: system → light → dark →
// (back to system). `system` follows the OS (prefers-color-scheme) and is the
// default until the user picks one; next-themes persists their choice. The
// button shows the icon of the CURRENT mode and advances to the next on click.
const ORDER = ['system', 'light', 'dark'] as const
type Mode = (typeof ORDER)[number]

const META: Record<Mode, { label: string; Icon: typeof Monitor }> = {
  system: { label: 'System theme', Icon: Monitor },
  light: { label: 'Light theme', Icon: Sun },
  dark: { label: 'Dark theme', Icon: Moon },
}

export default function ThemeToggle() {
  const { theme, setTheme } = useTheme()
  const [mounted, setMounted] = useState(false)

  // `theme` is unknown during SSR/first paint. Render an inert placeholder of
  // the same footprint until mounted to avoid a hydration mismatch / layout
  // shift, then show the real control.
  useEffect(() => setMounted(true), [])

  if (!mounted) {
    return <div className="h-7 w-7" aria-hidden="true" />
  }

  const current: Mode = (ORDER as readonly string[]).includes(theme ?? '')
    ? (theme as Mode)
    : 'system'
  const next = ORDER[(ORDER.indexOf(current) + 1) % ORDER.length]
  const { label, Icon } = META[current]

  return (
    // Lives in the always-dark `.nav-gradient` navbar (which forces white text on
    // every descendant). So it uses white-on-gradient styling, not the page-theme
    // `bg-muted` token — in light theme bg-muted is near-white and a forced-white
    // icon on it was invisible (the old "middle button isn't visible" bug).
    <button
      type="button"
      onClick={() => setTheme(next)}
      aria-label={`${label}. Switch to ${META[next].label.toLowerCase()}.`}
      title={`${label} — click to switch theme`}
      className="flex h-7 w-7 items-center justify-center rounded-md border border-white/30 text-white/80 transition-colors hover:bg-white/15 hover:text-white"
    >
      <Icon className="h-4 w-4" aria-hidden="true" />
    </button>
  )
}
